// Read a spreadsheet the user picked (programme import) into plain rows of
// text, in the browser, so the file never leaves the device on its own.
//
// XLSX is a zip of XML parts, and jszip is already a dependency, so this
// reads the parts itself rather than pulling in a spreadsheet library: the
// sheet names, the shared strings and each sheet's cells. Formula cells give
// their last calculated value, which is what the person sees in the sheet.
// CSV is split here too. Every cell comes back as a trimmed string.

import JSZip from "jszip";

const parseXml = (text) => new DOMParser().parseFromString(text, "application/xml");

// Elements by local name, whatever namespace prefix the writer used.
const byName = (node, name) => Array.from(node.getElementsByTagNameNS("*", name));

/** Column letters ("A", "AB") to a 0-based index. */
export const columnIndex = (ref) => {
  const letters = String(ref).match(/^[A-Z]+/i)?.[0].toUpperCase() || "A";
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
};

const cellText = (cell, shared) => {
  const type = cell.getAttribute("t");
  if (type === "inlineStr") {
    return byName(cell, "t").map((t) => t.textContent).join("");
  }
  const v = byName(cell, "v")[0]?.textContent ?? "";
  if (type === "s") return shared[Number(v)] ?? "";
  if (type === "b") return v === "1" ? "TRUE" : v === "0" ? "FALSE" : v;
  if (type === "e") return "";
  if (!type || type === "n") {
    // Round binary noise (0.30000000000000004) the way a sheet displays it.
    const num = Number(v);
    return v !== "" && Number.isFinite(num) ? String(Number(num.toPrecision(12))) : v;
  }
  return v;
};

const sheetRows = (xml, shared) => {
  const rows = [];
  for (const row of byName(parseXml(xml), "row")) {
    const r = Number(row.getAttribute("r")) - 1;
    const cells = [];
    for (const cell of byName(row, "c")) {
      const text = cellText(cell, shared).trim();
      if (text) cells[columnIndex(cell.getAttribute("r"))] = text;
    }
    rows[Number.isInteger(r) && r >= 0 ? r : rows.length] = Array.from(cells, (c) => c || "");
  }
  return Array.from(rows, (r) => r || []);
};

/**
 * An XLSX file's sheets, in workbook order.
 * @returns {Promise<{ name: string, rows: string[][] }[]>}
 */
export const readXlsx = async (data) => {
  const zip = await JSZip.loadAsync(data);
  const read = (path) => zip.file(path)?.async("string");
  const workbook = await read("xl/workbook.xml");
  if (!workbook) throw new Error("This doesn't look like an Excel workbook.");

  const sharedXml = await read("xl/sharedStrings.xml");
  const shared = sharedXml
    ? byName(parseXml(sharedXml), "si").map((si) =>
        byName(si, "t").map((t) => t.textContent).join(""),
      )
    : [];

  const relsXml = await read("xl/_rels/workbook.xml.rels");
  const targets = {};
  if (relsXml) {
    for (const rel of byName(parseXml(relsXml), "Relationship")) {
      const target = rel.getAttribute("Target") || "";
      targets[rel.getAttribute("Id")] = target.startsWith("/")
        ? target.slice(1)
        : `xl/${target.replace(/^\.\//, "")}`;
    }
  }

  const sheets = [];
  const entries = byName(parseXml(workbook), "sheet");
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const rid =
      entry.getAttribute("r:id") ||
      Array.from(entry.attributes).find((a) => a.localName === "id")?.value;
    const path = targets[rid] || `xl/worksheets/sheet${i + 1}.xml`;
    const xml = await read(path);
    if (xml == null) continue;
    sheets.push({ name: entry.getAttribute("name") || `Sheet ${i + 1}`, rows: sheetRows(xml, shared) });
  }
  return sheets;
};

/** Split CSV text into rows (quoted fields, doubled quotes, CRLF). */
export const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  const src = String(text || "").replace(/^﻿/, "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      row.push(field.trim());
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(field.trim());
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field || row.length) {
    row.push(field.trim());
    rows.push(row);
  }
  return rows;
};

/**
 * The sheets of a picked file: an XLSX workbook or a CSV (one sheet).
 * @param {File} file
 */
export const readSpreadsheetFile = async (file) => {
  const name = String(file?.name || "");
  if (/\.csv$/i.test(name) || file?.type === "text/csv") {
    return [{ name: name.replace(/\.csv$/i, "") || "Sheet 1", rows: parseCsv(await file.text()) }];
  }
  if (/\.xlsx$/i.test(name) || /spreadsheetml/.test(file?.type || "")) {
    return readXlsx(await file.arrayBuffer());
  }
  throw new Error("Pick an Excel (.xlsx) or CSV file.");
};
